import { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Images } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AlbumDetailPage() {
  const { albumId } = useParams();
  const [album, setAlbum] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/albums/${albumId}`)
      .then((r) => {
        if (!r.ok) throw new Error("not found");
        return r.json();
      })
      .then((data) => { setAlbum(data.album); setItems(data.items || []); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [albumId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="w-7 h-7 border-2 rounded-full animate-spin" style={{ borderColor: '#1A5C37', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  if (notFound || !album) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-center">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(26,92,55,0.08)' }}>
          <Images size={32} weight="duotone" style={{ color: '#1A5C37' }} />
        </div>
        <p className="text-sm font-medium" style={{ color: '#1C211F' }}>Album not found</p>
        <Link to="/gallery" className="text-xs mt-2" style={{ color: '#1A5C37' }}>← Back to gallery</Link>
      </div>
    );
  }

  return (
    <div data-testid="album-detail-page">
      {/* Page Header */}
      <section className="py-16 md:py-24 border-b" style={{ backgroundColor: '#E8EDE9', borderColor: '#D3DCD6' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Link
            to="/gallery"
            data-testid="back-to-albums"
            className="inline-flex items-center gap-1.5 text-xs font-medium mb-5"
            style={{ color: '#1A5C37' }}
          >
            <ArrowLeft size={14} weight="bold" />
            All Albums
          </Link>
          <p className="text-xs uppercase tracking-[0.2em] font-medium mb-3" style={{ color: '#B85C38' }}>Album</p>
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tighter font-medium" style={{ color: '#1C211F' }}>
            {album.name}
          </h1>
          <p className="text-sm mt-2" style={{ color: '#4A5450' }}>
            {items.length} {items.length === 1 ? 'image' : 'images'}
          </p>
        </div>
      </section>

      {/* Images Grid */}
      <section className="py-20 md:py-28" style={{ backgroundColor: '#F7F5F0' }}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {items.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(26,92,55,0.08)' }}>
                <Images size={32} weight="duotone" style={{ color: '#1A5C37' }} />
              </div>
              <p className="text-sm font-medium" style={{ color: '#1C211F' }}>No images in this album yet</p>
            </div>
          ) : (
            <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="break-inside-avoid rounded-xl overflow-hidden border cursor-pointer group"
                  style={{ borderColor: 'rgba(26,92,55,0.1)' }}
                  onClick={() => setLightbox({ url: `${BACKEND_URL}${item.image_url}`, title: item.title })}
                >
                  <div className="relative overflow-hidden">
                    <img
                      src={`${BACKEND_URL}${item.image_url}`}
                      alt={item.title}
                      className="w-full object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-end"
                      style={{ background: 'linear-gradient(to top, rgba(26,33,31,0.6) 0%, transparent 60%)' }}
                    >
                      <p className="text-sm font-medium px-4 pb-4" style={{ color: '#F7F5F0' }}>{item.title}</p>
                    </div>
                  </div>
                  {item.title && (
                    <div className="px-4 py-3" style={{ backgroundColor: '#fff' }}>
                      <p className="text-sm font-medium truncate" style={{ color: '#1C211F' }}>{item.title}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ backgroundColor: 'rgba(0,0,0,0.85)' }}
          onClick={() => setLightbox(null)}
        >
          <div className="relative max-w-4xl w-full" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setLightbox(null)} className="absolute -top-10 right-0 text-sm font-medium" style={{ color: '#F7F5F0' }}>
              Close ✕
            </button>
            <img src={lightbox.url} alt={lightbox.title} className="w-full rounded-xl object-contain max-h-[80vh]" />
            {lightbox.title && (
              <p className="text-center text-sm font-medium mt-3" style={{ color: '#F7F5F0' }}>{lightbox.title}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
